"use client"

import { useTranslations } from "next-intl"
import { cn, formatPrice } from "@/lib/utils"
import { ChatMessageItem } from "@/components/chat-message"
import { useChatScroll } from "@/hooks/use-chat-scroll"
import { type ChatMessage, useRealtimeChat } from "@/hooks/use-realtime-chat"
import { Loader2 } from "lucide-react"
import { PageLoadError } from "@/components/ui/page-load-error"
import { ChatConversationReportMenu } from "@/components/chat/chat-conversation-report-menu"
import { ChatStickyHeader, chatStickyHeaderPaddingTop } from "@/components/chat/chat-sticky-header"
import {
  chatConversationFooterFadeStyle,
} from "@/components/chat/chat-conversation-chrome-styles"
import { Skeleton } from "@/components/ui/skeleton"
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { motion } from "framer-motion"
import { createClient } from "@/utils/supabase/client"
import { getAvatarUrl } from "@/lib/getAvatarUrl"
import { useMobile } from "@/hooks/use-mobile"
import { BottomFixed } from "react-bottom-fixed"
import { useUnreadCount } from "@/hooks/use-unread-count"
import AuthModal from "@/components/ui/auth-modal"
import { getNormalizedImageFile, getSafeImageExtension } from "@/lib/image-upload"
import { upsertChatRoom } from "@/lib/chat/chat-upsert"
import {
  fallbackParticipantUsername,
  fetchChatParticipantProfiles,
  otherParticipantIdFromRoom,
} from "@/lib/chat/chat-participant-profile"
import { ChatSendError, sendChatMessage } from "@/lib/chat/chat-send"
import { ChatComposer, type PhotoAttachment } from "@/components/chat/ChatComposer"
import { ChatComposerReplyStrip } from "@/components/chat/ChatComposerReplyStrip"
import { ChatListingBottomSheet } from "@/components/chat/ChatListingBottomSheet"
import { ChatMessageOverlay } from "@/components/chat/ChatMessageOverlay"
import { MessagingGuidelinesSheet } from "@/components/chat/MessagingGuidelinesSheet"
import { MessagingSafetyBanner } from "@/components/chat/MessagingSafetyBanner"
import { ReportDialog } from "@/components/ui/report-dialog"
import { useChatMessageInteractions } from "@/hooks/use-chat-message-interactions"
import { useMessagingGuidelines } from "@/hooks/use-messaging-guidelines"
import { findMessageById, toReplyPreview } from "@/lib/chat/reply-preview"
import type { ChatReplyPreview } from "@/lib/chat/reply-preview"
import { shouldShowIncomingChatAvatar } from "@/lib/chat/chat-ui-tokens"
import type { MessageAnchorRect } from "@/hooks/use-chat-message-interactions"
import { toast } from "sonner"
import { CONTACT_BLOCK_GENERIC_ERROR, CONTACT_BLOCK_MESSAGE, VIOLATION_REASONS_SHORT } from "@/lib/contact-policy"
import { getModerationDisplayMessage, getModerationReason, type ViolationType } from "@/lib/moderation-client"
import { ModerationNotice } from "@/components/moderation/ModerationNotice"
import { OrderMessageCard } from "@/components/chat/OrderMessageCard"
import Link from "next/link"
import { CHAT_SELL_ITEM_ENABLED } from "@/lib/config"
import { ChatSellItemSheet } from "@/components/chat/ChatSellItemSheet"
import { GalleryOpenRefContext } from "@/components/chat/gallery-open-context"
import { ReportDialogOpenContext } from "@/components/chat/report-dialog-open-context"

class ModerationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ModerationError"
  }
}

interface RealtimeChatProps {
  roomName: string
  username: string
  onMessage?: (messages: ChatMessage[]) => void
  messages?: ChatMessage[]
  onBack?: () => void
  isDialog?: boolean
  dockToPanel?: boolean
  initialDraft?: { text?: string; imageFile?: File | null; listing?: any; order?: any; sendImmediately?: boolean } | null
  initialOtherProfile?: { username: string; avatar_url?: string | null } | null
  hideHeader?: boolean
  onNavigateAway?: (href: string) => void
  counterpartyBanned?: boolean
}

const IMAGE_GROUP_WINDOW_MS = 60 * 1000

const isImageOnlyMessage = (m: ChatMessage) =>
  Boolean(m.imageUrl) && (!m.content?.trim() || m.content.trim().toLowerCase() === "[image]")

const isListingMessage = (m: ChatMessage) => {
  const normalized = String(m.content ?? "").trim().toLowerCase()
  const hasListing = Boolean((m as any).listing)
  if (!hasListing) return false
  return normalized === "[listing]" || normalized.length === 0
}

type MessageEntry =
  | { type: "marker"; key: string; label: string }
  | { type: "message"; key: string; message: ChatMessage; index: number }
  | { type: "imageGroup"; key: string; messages: ChatMessage[]; firstIndex: number }
  | { type: "listingGroup"; key: string; messages: ChatMessage[]; firstIndex: number }

const DAY_IN_MS = 86_400_000

const startOfDay = (input: Date) => new Date(input.getFullYear(), input.getMonth(), input.getDate())

const getDateKey = (input: Date) => {
  const month = String(input.getMonth() + 1).padStart(2, "0")
  const day = String(input.getDate()).padStart(2, "0")
  return `${input.getFullYear()}-${month}-${day}`
}

const getDateLabel = (input: Date, labels: { today: string; yesterday: string }) => {
  if (Number.isNaN(input.getTime())) return ""
  const now = new Date()
  const diff = startOfDay(now).getTime() - startOfDay(input).getTime()
  const diffDays = Math.floor(diff / DAY_IN_MS)
  if (diffDays === 0) return labels.today
  if (diffDays === 1) return labels.yesterday
  if (diffDays > 1 && diffDays < 7) {
    return input.toLocaleDateString(undefined, { weekday: "long" })
  }
  const sameYear = now.getFullYear() === input.getFullYear()
  return input.toLocaleDateString(
    undefined,
    sameYear ? { month: "long", day: "numeric" } : { month: "long", day: "numeric", year: "numeric" }
  )
}


export const RealtimeChat = ({
  roomName,
  username,
  onMessage,
  messages: initialMessages = [],
  onBack,
  isDialog = false,
  dockToPanel = false,
  initialDraft = null,
  initialOtherProfile = null,
  hideHeader = false,
  onNavigateAway,
  counterpartyBanned = false,
}: RealtimeChatProps) => {
  const tChat = useTranslations("chat")
  const dateLabels = useMemo(() => ({ today: tChat("today"), yesterday: tChat("yesterday") }), [tChat])
  const isMobile = useMobile()
  const dockComposer = dockToPanel
  const composerLayer = dockComposer ? "absolute" : "fixed"
  const galleryContext = useContext(GalleryOpenRefContext)
  const reportDialogContext = useContext(ReportDialogOpenContext)
  const reportDialogOpen = reportDialogContext?.reportDialogOpen ?? false
  const { containerRef, scrollToBottom, isAtBottom } = useChatScroll()
  const { messages: realtimeMessages, sendMessage, replaceMessage, removeMessage, isConnected, isLoading, loadError, retryLoad, loadOlderMessages, isLoadingOlder, hasMoreMessages } = useRealtimeChat({ roomName, username })
  const { markConversationRead } = useUnreadCount(username)
  const [newMessage, setNewMessage] = useState("")
  const [composerError, setComposerError] = useState<{ message: string; type?: ViolationType } | null>(null)
  const [selfProfile, setSelfProfile] = useState<{ username: string; avatar_url?: string | null } | null>(null)
  const [otherProfile, setOtherProfile] = useState<{ username: string; avatar_url?: string | null } | null>(initialOtherProfile)
  const [selectedImages, setSelectedImages] = useState<PhotoAttachment[]>([])
  const [showListingSelector, setShowListingSelector] = useState(false)
  const [showSellSheet, setShowSellSheet] = useState(false)
  const [selectedListings, setSelectedListings] = useState<any[]>([])
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const supabase = useMemo(() => createClient(), [])
  const didInitialScroll = useRef(false)
  const lastMessageKeyRef = useRef<string | undefined>(undefined)
  const appliedInitialDraftRef = useRef(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const MAX_MESSAGE_LENGTH = 2000
  const composerRef = useRef<HTMLDivElement | null>(null)
  const [composerPadding, setComposerPadding] = useState(200)

  const canonicalRoomName = useMemo(() => {
    return roomName.includes(":") ? roomName.split(":").sort().join(":") : roomName
  }, [roomName])

  const otherParticipantId = useMemo(
    () => otherParticipantIdFromRoom(roomName, username),
    [roomName, username],
  )

  const otherParticipantFallbackName = useMemo(
    () => (otherParticipantId ? fallbackParticipantUsername(otherParticipantId) : "User"),
    [otherParticipantId],
  )

  const headerTitle = otherProfile?.username ?? otherParticipantFallbackName

  const replySenderContext = useMemo(() => {
    const participantNames: Record<string, string> = {}
    if (username && selfProfile?.username) participantNames[username] = selfProfile.username
    if (otherParticipantId && otherProfile?.username) participantNames[otherParticipantId] = otherProfile.username
    return { currentUserId: username, participantNames }
  }, [username, otherParticipantId, selfProfile?.username, otherProfile?.username])

  const {
    selectedMessageKey,
    overlayTarget,
    replyPreview,
    getMessageReactions,
    getViewerReaction,
    seedReactionsFromMessages,
    messageReportOpen,
    messageReportTarget,
    closeMessageReport,
    buildMessageReportPayload,
    openMessageOverlay,
    dismissOverlay,
    clearReply,
    handleOverlayReply,
    handleOverlayReport,
    handleOverlayCopy,
    handleReaction,
    applyReaction,
    resolveReplyPreview,
  } = useChatMessageInteractions(replySenderContext)

  const guidelines = useMessagingGuidelines(username || null)

  // Delivery-layer mapping for ChatSendError thrown by `sendChatMessage`. The
  // builder is wire-only; the toast / setComposerError / 422→ModerationError
  // / draft-requeue policy stays here per CLAUDE.md (no UI side-effects in
  // `lib/`). Mirror of the legacy parseSendResponse behaviour byte-for-byte.
  const handleChatSendError = useCallback((err: unknown): never => {
    if (!(err instanceof ChatSendError)) throw err
    let message =
      err.status === 422 ? CONTACT_BLOCK_MESSAGE : CONTACT_BLOCK_GENERIC_ERROR
    let violationType: ViolationType | undefined
    if (err.status === 422) {
      const display = getModerationDisplayMessage(err.serverError)
      message = display.message
      violationType = display.type
    } else if (err.serverError && err.serverError.trim().length > 0) {
      message = err.serverError
    }
    const shortReason =
      (violationType && VIOLATION_REASONS_SHORT[violationType]) || getModerationReason(message)
    setComposerError({ message, type: violationType })
    toast.error(shortReason ?? message)
    if (err.status === 422) {
      throw new ModerationError(message)
    }
    throw new Error(message)
  }, [setComposerError])

  const requeueDraft = useCallback((draft: string) => {
    if (!draft) return
    setNewMessage((prev) => (prev.trim().length > 0 ? prev : draft))
  }, [setNewMessage])

  const sendConfirmedTextMessage = useCallback(
    async (
      rawDraft: string,
      trimmedContent: string,
      pendingId?: string,
      extras?: {
        order?: any
        attachmentId?: string | null
        replyToMessageId?: string | null
        replyTo?: ChatReplyPreview | null
      },
    ) => {
      const replyToMessageId = extras?.replyToMessageId ?? replyPreview?.id ?? null
      await upsertChatRoom({ roomName: canonicalRoomName, participantIds: roomName.split(":") })
      const saved = await sendChatMessage({
        roomName: canonicalRoomName,
        content: trimmedContent,
        ...(extras?.order ? { order: extras.order } : {}),
        ...(extras?.attachmentId ? { attachmentId: extras.attachmentId } : {}),
        ...(replyToMessageId ? { replyToMessageId } : {}),
      }).catch(handleChatSendError)
      sendMessage(saved.content || trimmedContent, {
        id: saved.id,
        userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
        createdAt: saved.created_at,
        broadcastOnly: Boolean(pendingId),
        order: extras?.order ?? saved.order,
        attachmentId: saved.attachment_id ?? extras?.attachmentId ?? null,
        replyToMessageId,
        replyTo: extras?.replyTo ?? replyPreview ?? null,
      })
      if (replyToMessageId) clearReply()
      setComposerError(null)
      return saved
    },
    [canonicalRoomName, clearReply, handleChatSendError, replyPreview, roomName, sendMessage, selfProfile, username],
  )

  const createPendingTextBubble = useCallback(
    (content: string, attachmentId?: string | null, replyToMessageId?: string | null, replyTo?: ChatReplyPreview | null) => {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
      sendMessage(content, {
        id: tempId,
        userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
        pending: true,
        localOnly: true,
        attachmentId: attachmentId ?? null,
        replyToMessageId: replyToMessageId ?? null,
        replyTo: replyTo ?? null,
      })
      return tempId
    },
    [sendMessage, selfProfile, username],
  )

  useEffect(() => {
    if (!composerRef.current) return
    let raf = 0
    const measure = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = 0
        const height = composerRef.current?.offsetHeight ?? 0
        setComposerPadding(height + 24)
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(composerRef.current)
    return () => {
      observer.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [selectedImages.length, selectedListings.length, selectedOrder, showListingSelector, replyPreview, composerError, guidelines.showSafetyBanner])

  const composerHasOpaqueStack = Boolean(
    replyPreview || composerError || guidelines.showSafetyBanner,
  )

  const allMessages = useMemo(() => {
    const merged = [...initialMessages, ...realtimeMessages]
    const unique = merged.filter((m, i, self) => i === self.findIndex((x) => (x.clientKey ?? x.id) === (m.clientKey ?? m.id)))
    const dependsOn = (m: any, target: any) => {
      const ref = m.attachmentId || (m as any).attachment_id
      const targetKey = target.clientKey ?? target.id
      return ref && targetKey && ref === targetKey
    }
    return unique.sort((a: any, b: any) => {
      const ta = new Date(a.createdAt).getTime()
      const tb = new Date(b.createdAt).getTime()

      if (dependsOn(a, b)) return 1
      if (dependsOn(b, a)) return -1

      if (ta !== tb) {
        return ta - tb
      }
      const ka = (a.clientKey ?? a.id ?? '') as string
      const kb = (b.clientKey ?? b.id ?? '') as string
      return ka.localeCompare(kb)
    })
  }, [initialMessages, realtimeMessages])

  useEffect(() => {
    seedReactionsFromMessages(allMessages)
  }, [allMessages, seedReactionsFromMessages])

  const setReportDialogOpenRef = useRef(reportDialogContext?.setReportDialogOpen)
  setReportDialogOpenRef.current = reportDialogContext?.setReportDialogOpen

  useEffect(() => {
    setReportDialogOpenRef.current?.(messageReportOpen)
  }, [messageReportOpen])

  const replyFallbackSenderName = useMemo(() => {
    if (!replyPreview) return "User"
    const isOwn = replyPreview.senderId === username
    if (isOwn) return selfProfile?.username ?? "You"
    return otherProfile?.username ?? "User"
  }, [replyPreview, username, selfProfile?.username, otherProfile?.username])

  const getMessageInteractionProps = useCallback(
    (
      message: ChatMessage,
      isOwn: boolean,
      displayName: string,
      avatarUrl: string | null | undefined,
      messageKey: string,
      showAvatar: boolean,
      showTail: boolean,
    ) => {
      const replyToForBubble = (() => {
        const resolved = resolveReplyPreview(message)
        if (resolved) return resolved
        if (!message.replyToMessageId) return null
        const parent = findMessageById(allMessages, message.replyToMessageId)
        return parent ? toReplyPreview(parent, replySenderContext) : null
      })()
      const reportCtx = {
        isOwnMessage: isOwn,
        reportedUsername: displayName || null,
        reportedAvatarUrl: avatarUrl ?? null,
        itemTitle: displayName ? `@${displayName}` : undefined,
      }
      const isOverlaySource = selectedMessageKey === messageKey && Boolean(overlayTarget)
      return {
        replyPreview: replyToForBubble,
        reactions: getMessageReactions(message),
        viewerReaction: getViewerReaction(message),
        onReactionToggle: message.id ? (emoji: string) => applyReaction(message.id, emoji) : undefined,
        onOpenMessageMenu: message.pending
          ? undefined
          : (anchor: MessageAnchorRect) =>
              openMessageOverlay(message, reportCtx, messageKey, anchor, {
                showAvatar,
                showTail,
                replyPreview: replyToForBubble,
              }),
        replySenderContext,
        hideForOverlay: isOverlaySource,
      }
    },
    [
      allMessages,
      applyReaction,
      getMessageReactions,
      getViewerReaction,
      openMessageOverlay,
      overlayTarget,
      replySenderContext,
      resolveReplyPreview,
      selectedMessageKey,
    ],
  )

  useEffect(() => { if (onMessage) onMessage(allMessages) }, [allMessages, onMessage])

  // Reset initial scroll flag when switching rooms
  useEffect(() => { didInitialScroll.current = false }, [roomName])

  // Scroll to bottom when messages load initially
  useEffect(() => {
    if (!isLoading && allMessages.length > 0 && !didInitialScroll.current) {
      const timer = setTimeout(() => {
        scrollToBottom()
        didInitialScroll.current = true
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [isLoading, allMessages.length, scrollToBottom])

  // Keep initial hint in sync when room changes
  useEffect(() => {
    if (initialOtherProfile) setOtherProfile((prev) => prev ?? initialOtherProfile)
  }, [initialOtherProfile])

  useEffect(() => {
    if (!username || !roomName) return
    let mounted = true
    ;(async () => {
      try {
        const profileIds = [username, otherParticipantId].filter(Boolean) as string[]
        const profiles = await fetchChatParticipantProfiles(supabase, username, profileIds)
        if (!mounted) return

        const self = profiles.get(username)
        if (self) setSelfProfile({ username: self.username, avatar_url: self.avatar_url })

        if (otherParticipantId) {
          const other = profiles.get(otherParticipantId)
          if (other) {
            setOtherProfile((prev) => prev ?? other)
          } else {
            setOtherProfile((prev) =>
              prev ?? {
                username: fallbackParticipantUsername(otherParticipantId),
                avatar_url: null,
              },
            )
          }
        }
      } catch (error) {
        console.error("Failed to fetch profiles:", error)
      }
    })()
    return () => {
      mounted = false
    }
  }, [supabase, username, roomName, otherParticipantId])

  // (moved below submitMessage)

  // Auto-follow new messages: if already at bottom or it's our own message
  useEffect(() => {
    if (!allMessages.length) return
    const last = allMessages[allMessages.length - 1]
    const key = (last.clientKey ?? last.id) as string | undefined
    if (key === lastMessageKeyRef.current) return
    lastMessageKeyRef.current = key

    const isOwn = (last.senderId || last.user.name) === username
  if (isOwn || isAtBottom()) {
      const t = setTimeout(() => scrollToBottom(), 0)
      return () => clearTimeout(t)
    }
  }, [allMessages, username, isAtBottom, scrollToBottom])

  // If at bottom and viewport changes (keyboard/resize), keep anchored
  useEffect(() => {
    const onResize = () => { if (isAtBottom()) scrollToBottom() }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isAtBottom, scrollToBottom])

  // When keyboard inset changes, keep anchored if user was at bottom
  useEffect(() => {
    if (isAtBottom()) {
      const t = setTimeout(() => scrollToBottom(), 0)
      return () => clearTimeout(t)
    }
  }, [])

  useEffect(() => {
    if (!canonicalRoomName) return
    markConversationRead(canonicalRoomName)
  }, [canonicalRoomName, markConversationRead])

  useEffect(() => {
    if (!canonicalRoomName) return
    if (!allMessages.length) return
    const last = allMessages[allMessages.length - 1]
    const sender = last.senderId || last.user.name
    if (sender && sender !== username) {
      markConversationRead(canonicalRoomName)
    }
  }, [allMessages, canonicalRoomName, markConversationRead, username])

  useEffect(() => {
    return () => {
      if (canonicalRoomName) {
        markConversationRead(canonicalRoomName)
      }
    }
  }, [canonicalRoomName, markConversationRead])

  // Infinite scroll: Load older messages when scrolling near the top
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let raf: number | null = null

    const handleScroll = () => {
      const scrollTop = container.scrollTop
      const threshold = 100 // pixels from top

      // If scrolled near top and we have more messages to load
      if (scrollTop <= threshold && hasMoreMessages && !isLoadingOlder && !isLoading) {
        const previousScrollHeight = container.scrollHeight
        const previousScrollTop = container.scrollTop
        
        loadOlderMessages().then(() => {
          // Preserve scroll position after loading older messages
          // This prevents the jump that would occur from prepending messages
          raf = requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight
            const heightDifference = newScrollHeight - previousScrollHeight
            container.scrollTop = previousScrollTop + heightDifference
          })
        })
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (raf !== null) cancelAnimationFrame(raf)
    }
  }, [containerRef, hasMoreMessages, isLoadingOlder, isLoading, loadOlderMessages])

  // When returning to the default composer from attachments, keep anchored at bottom
  // Removed as we no longer have attachments mode

  // When returning from attachments to default composer, also ensure anchored
  // Removed as we no longer have attachments mode

  // Removed dynamic composer height observer
  // Removed composer observer (rollback)

  const submitMessage = useCallback(async () => {
    // Guard: if somehow unauthenticated, prompt login instead of trying to send
    if (!username) {
      setShowAuthModal(true)
      return
    }
    const draftSnapshot = newMessage
    const trimmed = draftSnapshot.trim()
    if (!trimmed && selectedImages.length === 0 && selectedListings.length === 0 && !selectedOrder) return

    const hasAttachments = selectedImages.length > 0 || selectedListings.length > 0 || !!selectedOrder

    if (!hasAttachments) {
      if (!trimmed) return
      const replyToMessageId = replyPreview?.id ?? null
      const pendingTextId = createPendingTextBubble(trimmed, null, replyToMessageId, replyPreview)
      setNewMessage("")
      scrollToBottom()
      try {
        const saved = await sendConfirmedTextMessage(draftSnapshot, trimmed, pendingTextId, {
          replyToMessageId,
          replyTo: replyPreview,
        })
        replaceMessage(pendingTextId, {
          id: saved.id,
          content: saved.content,
          createdAt: saved.created_at,
          pending: false,
          replyToMessageId,
          replyTo: replyPreview,
        })
        setComposerError(null)
      } catch (error) {
        removeMessage(pendingTextId)
        if (!(error instanceof ModerationError)) {
          requeueDraft(draftSnapshot)
        }
      }
      return
    }

    const imagesSnapshot = [...selectedImages]
    const listingsSnapshot = [...selectedListings]
    const orderSnapshot = selectedOrder
    setNewMessage("")
    setSelectedImages([])
    setSelectedListings([])
    setSelectedOrder(null)
    setShowListingSelector(false)
    scrollToBottom()

    const canonicalRoomName = roomName.split(":").sort().join(":")
    const roomParticipants = roomName.split(":").filter(Boolean)
    const otherParticipantId = roomParticipants.find((p) => p !== username) || null

    const persistOneImage = async (img: PhotoAttachment, content: string) => {
      const tempImageId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
      sendMessage("[Image]", {
        id: tempImageId,
        userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
        imageUrl: img.previewUrl,
        pending: true,
        localOnly: true,
      })
      const pendingTextId = content.trim() ? createPendingTextBubble(content.trim(), tempImageId) : null
      try {
        let imageToUpload: File = img.file
        try {
          const normalized = await getNormalizedImageFile(img.file)
          imageToUpload = normalized.file
        } catch {}
        const formData = new FormData()
        formData.append("image", imageToUpload)
        formData.append("roomName", roomName)
        const uploadResponse = await fetch("/api/chat/upload-image", { method: "POST", body: formData })
        const uploadPayload = await uploadResponse.json().catch(() => null)
        if (!uploadResponse.ok) {
          if (uploadResponse.status === 422) {
            const display = getModerationDisplayMessage(uploadPayload?.error)
            setComposerError({ message: display.message, type: display.type })
            toast.error((display.type && VIOLATION_REASONS_SHORT[display.type]) || getModerationReason(display.message))
            removeMessage(tempImageId)
            if (pendingTextId) removeMessage(pendingTextId)
            return
          }
          throw new Error(uploadPayload?.error || "Failed to upload image")
        }
        const { imageUrl } = uploadPayload
        await upsertChatRoom({ roomName: canonicalRoomName, participantIds: roomName.split(":") })
        const saved = await sendChatMessage({
          roomName: canonicalRoomName,
          content: "[Image]",
          imageUrl,
        }).catch(handleChatSendError)
        replaceMessage(tempImageId, { id: saved.id, content: saved.content, createdAt: saved.created_at, imageUrl: saved.image_url ?? undefined, pending: false })
        setComposerError(null)
        sendMessage(saved.content || "[Image]", {
          id: saved.id,
          imageUrl: saved.image_url,
          userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
          broadcastOnly: true,
          createdAt: saved.created_at,
        })
        if (pendingTextId) {
          try {
            const savedText = await sendConfirmedTextMessage(content.trim(), content.trim(), pendingTextId, { attachmentId: saved.id })
            replaceMessage(pendingTextId, { id: savedText.id, content: savedText.content, createdAt: savedText.created_at, pending: false, attachmentId: saved.id })
          } catch (err) {
            removeMessage(pendingTextId)
            if (!(err instanceof ModerationError)) requeueDraft(content.trim())
          }
        }
      } catch (error) {
        if (error instanceof ModerationError) {
          removeMessage(tempImageId)
          if (pendingTextId) removeMessage(pendingTextId)
        } else {
          replaceMessage(tempImageId, { id: tempImageId, content: "[Upload failed]", createdAt: new Date().toISOString(), imageUrl: undefined, pending: false })
          if (pendingTextId) removeMessage(pendingTextId)
          requeueDraft(content.trim())
        }
      } finally {
        try { URL.revokeObjectURL(img.previewUrl) } catch {}
      }
    }

    const persistOneListing = async (listing: any, content: string) => {
      // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
      const isChatPrivate = listing.visibility === "chat_private"
      const listingToSend = {
        id: listing.id,
        user_id: listing.user_id ?? otherParticipantId ?? null,
        visibility: isChatPrivate ? "chat_private" : "public",
        chat_private_cancelled_at: isChatPrivate ? null : listing.chat_private_cancelled_at ?? null,
        deleted_at: listing.deleted_at ?? null,
        title: listing.title,
        description: listing.description ?? "",
        price: Number(listing.price ?? 0),
        currency: listing.currency ?? null,
        quantity: isChatPrivate ? Number(listing.quantity ?? 0) + 1 : listing.quantity ?? 1,
        image_urls: listing.image_urls,
        shipping_config: Array.isArray(listing.shipping_config) && listing.shipping_config.length > 0 ? listing.shipping_config : (Array.isArray(listing.shipping_config) ? listing.shipping_config : null),
        condition: listing.condition,
        grade: listing.grade,
        // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: structured trio carried on
        // the chat payload; `/api/chat/send` and the receiving renderer
        // both read `shipFromAddress`. Server resolves pin → main fallback.
        shipFromAddress: listing.shipFromAddress ?? null,
        seller_username: listing.seller_username ?? initialOtherProfile?.username ?? selfProfile?.username ?? username,
        seller_avatar_url: listing.seller_avatar_url ?? initialOtherProfile?.avatar_url ?? selfProfile?.avatar_url ?? null,
      }
      const tempListingId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
      sendMessage("[Listing]", {
        id: tempListingId,
        userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
        listing: listingToSend,
        pending: true,
        localOnly: true,
      })
      const pendingTextId = content.trim() ? createPendingTextBubble(content.trim(), tempListingId) : null
      try {
        await upsertChatRoom({ roomName: canonicalRoomName, participantIds: roomName.split(":") })
        const saved = await sendChatMessage({
          roomName: canonicalRoomName,
          content: "[Listing]",
          listing: listingToSend,
        }).catch(handleChatSendError)
        const listingFromResponse = saved.listing ?? listingToSend
        replaceMessage(tempListingId, { id: saved.id, content: saved.content, createdAt: saved.created_at, listing: listingFromResponse, pending: false })
        setComposerError(null)
        sendMessage(saved.content || "[Listing]", {
          id: saved.id,
          listing: listingFromResponse,
          userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
          broadcastOnly: true,
          createdAt: saved.created_at,
        })
        if (pendingTextId) {
          try {
            const savedText = await sendConfirmedTextMessage(content.trim(), content.trim(), pendingTextId, { attachmentId: saved.id })
            replaceMessage(pendingTextId, { id: savedText.id, content: savedText.content, createdAt: savedText.created_at, pending: false, attachmentId: saved.id })
          } catch (err) {
            removeMessage(pendingTextId)
            if (!(err instanceof ModerationError)) requeueDraft(content.trim())
          }
        }
      } catch (error) {
        if (error instanceof ModerationError) {
          removeMessage(tempListingId)
          if (pendingTextId) removeMessage(pendingTextId)
        } else {
          replaceMessage(tempListingId, { id: tempListingId, content: "[Listing failed to send]", createdAt: new Date().toISOString(), listing: undefined, pending: false })
          if (pendingTextId) removeMessage(pendingTextId)
          requeueDraft(content.trim())
        }
      }
    }

    const persistOneOrder = async (order: any, content: string) => {
      const orderToSend = {
        id: order.orderId ?? order.id ?? order.orderNumber ?? null,
        orderId: order.orderId ?? order.id ?? order.orderNumber ?? null,
        orderNumber: order.orderNumber ?? order.id ?? order.orderId ?? null,
        sellerOrderId: order.sellerOrderId ?? order.seller_order_id ?? null,
        total: order.total,
        status: order.status,
        date: order.date,
        itemCount: order.itemCount,
        firstItemImage: order.firstItemImage,
        firstItemTitle: order.firstItemTitle,
        buyerName: order.buyerName,
        sellerName: order.sellerName,
        buyerAvatar: order.buyerAvatar,
        sellerAvatar: order.sellerAvatar,
      }
      const tempOrderId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
      sendMessage("[Order]", {
        id: tempOrderId,
        userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
        order: orderToSend,
        pending: true,
        localOnly: true,
      })
      const pendingTextId = content.trim() ? createPendingTextBubble(content.trim(), tempOrderId) : null
      try {
        await upsertChatRoom({ roomName: canonicalRoomName, participantIds: roomName.split(":") })
        const saved = await sendChatMessage({
          roomName: canonicalRoomName,
          content: "[Order]",
          order: orderToSend,
        }).catch(handleChatSendError)
        const hydratedOrder = saved.order ?? orderToSend
        replaceMessage(tempOrderId, { id: saved.id, content: saved.content, createdAt: saved.created_at, order: hydratedOrder, pending: false })
        sendMessage(saved.content || "[Order]", {
          id: saved.id,
          order: hydratedOrder,
          userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
          broadcastOnly: true,
          createdAt: saved.created_at,
        })
        if (pendingTextId) {
          try {
            const savedText = await sendConfirmedTextMessage(content.trim(), content.trim(), pendingTextId, { attachmentId: saved.id })
            replaceMessage(pendingTextId, { id: savedText.id, content: savedText.content, createdAt: savedText.created_at, pending: false, attachmentId: saved.id })
          } catch (err) {
            removeMessage(pendingTextId)
            if (!(err instanceof ModerationError)) requeueDraft(content.trim())
          }
        }
      } catch (error) {
        if (error instanceof ModerationError) {
          removeMessage(tempOrderId)
          if (pendingTextId) removeMessage(pendingTextId)
        } else {
          replaceMessage(tempOrderId, { id: tempOrderId, content: "[Envoi échoué]", createdAt: new Date().toISOString(), order: undefined, pending: false })
          if (pendingTextId) removeMessage(pendingTextId)
          requeueDraft(content.trim())
        }
      }
    }

    ;(async () => {
      let textLeft = trimmed
      for (const img of imagesSnapshot) {
        await persistOneImage(img, textLeft)
        textLeft = ""
      }
      for (const list of listingsSnapshot) {
        await persistOneListing(list, textLeft)
        textLeft = ""
      }
      if (orderSnapshot) {
        await persistOneOrder(orderSnapshot, textLeft)
      }
    })()
  }, [newMessage, selectedImages, selectedListings, selectedOrder, sendMessage, selfProfile, username, scrollToBottom, roomName, replaceMessage, handleChatSendError, removeMessage, requeueDraft, sendConfirmedTextMessage, createPendingTextBubble, initialOtherProfile, replyPreview])

  const sendListingDirect = useCallback((listing: any) => {
    if (!listing?.id) return
    // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
    const isChatPrivate = listing.visibility === "chat_private"
    const listingToSend = {
      id: listing.id,
      user_id: listing.user_id ?? undefined,
      visibility: isChatPrivate ? "chat_private" : "public",
      chat_private_cancelled_at: isChatPrivate ? null : listing.chat_private_cancelled_at ?? null,
      deleted_at: listing.deleted_at ?? null,
      title: listing.title ?? "Listing",
      description: listing.description ?? "",
      price: listing.price ?? 0,
      currency: listing.currency ?? null,
      quantity: isChatPrivate ? Number(listing.quantity ?? 0) + 1 : listing.quantity ?? 1,
      image_urls: listing.image_urls ?? [],
      shipping_config: listing.shipping_config ?? null,
      condition: listing.condition ?? "",
      grade: listing.grade ?? "",
      // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: see persistOneListing above.
      shipFromAddress: listing.shipFromAddress ?? null,
      seller_username: selfProfile?.username || username,
      seller_avatar_url: selfProfile?.avatar_url || null,
    }

    const tempListingId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
    sendMessage("[Listing]", {
      id: tempListingId,
      userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
      listing: listingToSend,
      pending: true,
      localOnly: true,
    })

    scrollToBottom()

    ;(async () => {
      try {
        const canonicalRoomName = roomName.split(":").sort().join(":")
        await upsertChatRoom({ roomName: canonicalRoomName, participantIds: roomName.split(":") })
        const saved = await sendChatMessage({
          roomName: canonicalRoomName,
          content: "[Listing]",
          listing: listingToSend,
        }).catch(handleChatSendError)
        const listingFromResponse = saved.listing ?? listingToSend
        replaceMessage(tempListingId, {
          id: saved.id,
          content: saved.content,
          createdAt: saved.created_at,
          listing: listingFromResponse,
          pending: false,
        })
        sendMessage(saved.content || "[Listing]", {
          id: saved.id,
          listing: listingFromResponse,
          userOverride: { name: selfProfile?.username || username, avatar_url: selfProfile?.avatar_url || null },
          broadcastOnly: true,
          createdAt: saved.created_at,
        })
      } catch (error) {
        if (error instanceof ModerationError) {
          removeMessage(tempListingId)
        } else {
          replaceMessage(tempListingId, { id: tempListingId, content: "[Listing failed to send]", createdAt: new Date().toISOString(), listing: undefined, pending: false })
        }
      }
    })()
  }, [handleChatSendError, removeMessage, replaceMessage, roomName, scrollToBottom, selfProfile, sendMessage, username])

  const shareListingFromSell = useCallback((listing: any) => {
    sendListingDirect(listing)
  }, [sendListingDirect])

  const addImages = useCallback((files: FileList | null) => {
    if (!files?.length) return
    const newOnes: PhotoAttachment[] = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    setSelectedImages((prev) => [...prev, ...newOnes])
  }, [])

  const removeImage = useCallback((id: string) => {
    setSelectedImages((prev) => {
      const found = prev.find((p) => p.id === id)
      if (found) try { URL.revokeObjectURL(found.previewUrl) } catch {}
      return prev.filter((p) => p.id !== id)
    })
  }, [])

  const toggleListing = useCallback((listing: any) => {
    setSelectedListings((prev) => {
      const exists = prev.some((l) => l.id === listing.id)
      if (exists) return prev.filter((l) => l.id !== listing.id)
      return [...prev, listing]
    })
  }, [])

  const removeListing = useCallback((id: string) => {
    setSelectedListings((prev) => prev.filter((l) => l.id !== id))
  }, [])

  // Auto-send initial draft (from inbox compose) using the same optimistic flow
  useEffect(() => {
    if (!initialDraft || appliedInitialDraftRef.current) return
    appliedInitialDraftRef.current = true
    let timer: ReturnType<typeof setTimeout> | null = null
    let createdPreview: string | null = null

    const maybeSend = async () => {
      try {
        const shouldSend = initialDraft.sendImmediately !== false
        if (initialDraft.imageFile) {
          createdPreview = URL.createObjectURL(initialDraft.imageFile)
          setSelectedImages([{ id: crypto.randomUUID(), file: initialDraft.imageFile, previewUrl: createdPreview }])
          if (initialDraft.text) setNewMessage(initialDraft.text)
          if (shouldSend) timer = setTimeout(() => submitMessage(), 0)
        } else if (initialDraft.listing) {
          setSelectedListings([initialDraft.listing])
          if (initialDraft.text) setNewMessage(initialDraft.text)
          if (shouldSend) timer = setTimeout(() => submitMessage(), 0)
        } else if (initialDraft.order) {
          setSelectedOrder(initialDraft.order)
          if (initialDraft.text) setNewMessage(initialDraft.text)
          if (shouldSend) timer = setTimeout(() => { submitMessage() }, 0)
        } else if (initialDraft.text && initialDraft.text.trim()) {
          setNewMessage(initialDraft.text)
          if (shouldSend) timer = setTimeout(() => { submitMessage() }, 0)
        }
      } catch {}
    }
    maybeSend()
    return () => {
      if (timer) clearTimeout(timer)
      if (createdPreview) {
        try { URL.revokeObjectURL(createdPreview) } catch {}
      }
    }
  }, [initialDraft, submitMessage])

  const messageEntries = useMemo<MessageEntry[]>(() => {
    const rawEntries: MessageEntry[] = []
    let lastDateKey: string | null = null
    allMessages.forEach((message, index) => {
      const reactKey = String(message.clientKey ?? message.id ?? `message-${index}`)
      const createdAt = new Date(message.createdAt)
      if (!Number.isNaN(createdAt.getTime())) {
        const dateKey = getDateKey(createdAt)
        if (dateKey !== lastDateKey) {
          const label = getDateLabel(createdAt, dateLabels) || createdAt.toLocaleDateString()
          rawEntries.push({ type: "marker", key: `date-${dateKey}`, label })
          lastDateKey = dateKey
        }
      }
      rawEntries.push({ type: "message", key: reactKey, message, index })
    })
    const result: MessageEntry[] = []
    let i = 0
    while (i < rawEntries.length) {
      const entry = rawEntries[i]
      if (entry.type === "marker") {
        result.push(entry)
        i++
        continue
      }
      if (entry.type !== "message") {
        result.push(entry)
        i++
        continue
      }
      const senderId = entry.message.senderId || entry.message.user?.name
      const startTime = new Date(entry.message.createdAt).getTime()

      if (isImageOnlyMessage(entry.message)) {
        const group: ChatMessage[] = [entry.message]
        let j = i + 1
        let prevTime = startTime
        while (j < rawEntries.length) {
          const next = rawEntries[j]
          if (next.type !== "message") break
          const nextMsg = next.message
          const nextSender = nextMsg.senderId || nextMsg.user?.name
          const nextTime = new Date(nextMsg.createdAt).getTime()
          if (isImageOnlyMessage(nextMsg) && nextSender === senderId && nextTime - prevTime <= IMAGE_GROUP_WINDOW_MS) {
            group.push(nextMsg)
            prevTime = nextTime
            j++
            continue
          }
          break
        }
        if (group.length > 1) {
          result.push({ type: "imageGroup", key: `img-${entry.key}`, messages: group, firstIndex: entry.index })
          i = j
          continue
        }
      }

      if (isListingMessage(entry.message)) {
        const group: ChatMessage[] = [entry.message]
        let j = i + 1
        let prevTime = startTime
        while (j < rawEntries.length) {
          const next = rawEntries[j]
          if (next.type !== "message") break
          const nextMsg = next.message
          const nextSender = nextMsg.senderId || nextMsg.user?.name
          const nextTime = new Date(nextMsg.createdAt).getTime()
          if (isListingMessage(nextMsg) && nextSender === senderId && nextTime - prevTime <= IMAGE_GROUP_WINDOW_MS) {
            group.push(nextMsg)
            prevTime = nextTime
            j++
            continue
          }
          break
        }
        if (group.length > 1) {
          result.push({ type: "listingGroup", key: `lst-${entry.key}`, messages: group, firstIndex: entry.index })
          i = j
          continue
        }
      }

      result.push(entry)
      i++
    }
    return result
  }, [allMessages])

  return (
    <div 
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col bg-background text-foreground antialiased",
        dockComposer && "overflow-hidden",
        isDialog && "bg-transparent"
      )}
      style={{ 
        touchAction: 'pan-y', // Only allow vertical scrolling within chat
        overscrollBehavior: 'contain' // Prevent scroll chaining to parent
      }}
      onWheel={(e) => {
        // Prevent scroll events from bubbling to parent when in overlay
        if (isDialog) {
          e.stopPropagation()
        }
      }}
      onTouchMove={(e) => {
        // Prevent touch scroll events from bubbling to parent when in overlay
        if (isDialog) {
          e.stopPropagation()
        }
      }}
    >
      <ChatSellItemSheet
        open={showSellSheet}
        onOpenChangeAction={setShowSellSheet}
        currentUserId={username}
        roomName={roomName}
        onShareListingAction={shareListingFromSell}
      />
      {!hideHeader ? (
        <ChatStickyHeader
          title={headerTitle}
          avatarUrl={otherProfile?.avatar_url ?? null}
          showParticipantChrome
          onBack={onBack}
          position={dockComposer ? "absolute" : "fixed"}
          surface={dockComposer ? "panel" : "overlay"}
          scrollContainerRef={containerRef}
          trailing={
            <ChatConversationReportMenu
              roomName={roomName}
              currentUserId={username}
              otherProfile={otherProfile}
            />
          }
        />
      ) : null}
    <div 
        ref={containerRef}
        className={cn("min-h-0 flex-1 overflow-y-auto chat-messages-surface", hideHeader && "pt-4")}
        style={{
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          paddingTop: hideHeader ? undefined : chatStickyHeaderPaddingTop(),
          paddingBottom: composerPadding,
          transition: 'padding-bottom 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onWheel={(e) => {
          if (isDialog) {
            e.stopPropagation()
          }
        }}
      >
            <div className="px-5 py-2 space-y-3 min-w-0">
        {/* Loading older messages indicator */}
        {isLoadingOlder && (
          <div className="flex justify-center py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading older messages...
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {/* Header row for first message */}
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-40 rounded-2xl" />
              </div>
            </div>
            {/* A few alternating bubbles */}
            <div className="flex justify-end">
              <Skeleton className="h-6 w-48 rounded-2xl" />
            </div>
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-56 rounded-2xl" />
            </div>
            <div className="flex justify-end">
              <Skeleton className="h-24 w-60 rounded-lg" />
            </div>
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-40 rounded-2xl" />
                <Skeleton className="h-6 w-28 rounded-2xl" />
              </div>
            </div>
          </div>
        ) : loadError ? (
          <PageLoadError
            title={tChat("loadErrorTitle")}
            subtitle={tChat("loadErrorSubtitle")}
            detail={loadError.message}
            reloadLabel={tChat("reload")}
            backLabel={tChat("loadErrorBack")}
            onReload={() => void retryLoad()}
            onBack={onBack}
            className="min-h-[50vh]"
          />
        ) : allMessages.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center text-sm text-muted-foreground">No messages yet. Start the conversation!</div>
          </div>
        ) : null}
        {!isLoading ? (
        <div>
          {messageEntries.map((entry) => {
            if (entry.type === "marker") {
              return (
                <div key={entry.key} className="py-4">
                  <div className="flex items-center gap-3 text-[11px] font-semibold text-muted-foreground">
                    <div className="flex-1 h-px bg-border/70" />
                    <span className="px-3 py-1 rounded-full bg-muted/60">{entry.label}</span>
                    <div className="flex-1 h-px bg-border/70" />
                  </div>
                </div>
              )
            }
            if (entry.type === "imageGroup") {
              const lead = entry.messages[0]!
              const lastInGroup = entry.messages[entry.messages.length - 1]!
              const isOwn = (lead.senderId || lead.user?.name) === username
              const name = isOwn ? (selfProfile?.username || "You") : (otherProfile?.username || "User")
              const avatar = isOwn ? null : getAvatarUrl(lead.user?.avatar_url || otherProfile?.avatar_url || "")
              const prevMessage = entry.firstIndex > 0 ? allMessages[entry.firstIndex - 1] : null
              const afterIndex = entry.firstIndex + entry.messages.length
              const afterMessage = afterIndex < allMessages.length ? allMessages[afterIndex] : null
              const senderKey = lastInGroup.senderId || lastInGroup.user?.name
              const sameSenderAfter = Boolean(
                afterMessage && (afterMessage.senderId || afterMessage.user?.name) === senderKey,
              )
              const sameDayAfter = Boolean(
                afterMessage &&
                  getDateKey(new Date(afterMessage.createdAt)) === getDateKey(new Date(lastInGroup.createdAt)),
              )
              const showAvatar = shouldShowIncomingChatAvatar({
                isOwnMessage: isOwn,
                sameSenderNext: sameSenderAfter,
                sameDayNext: sameDayAfter,
              })
              const isNewSender = !prevMessage || prevMessage.user?.name !== lead.user?.name
              const wrapperClass = entry.firstIndex === 0 ? "" : isNewSender ? "mt-4" : "mt-1"
              const leadKey = String(lead.clientKey ?? lead.id)
              const interactionProps = getMessageInteractionProps(lead, isOwn, name, avatar, leadKey, showAvatar, false)
              return (
                <div key={entry.key} className={wrapperClass}>
                  <ChatMessageItem
                    message={lead}
                    isOwnMessage={isOwn}
                    showHeader={showAvatar}
                    showAvatar={showAvatar}
                    displayName={name}
                    avatarUrl={avatar}
                    roomName={roomName}
                    currentUserId={username}
                    otherParticipantId={otherParticipantId}
                    showTail={false}
                    onOrderCardClick={onNavigateAway}
                    imageGroup={entry.messages}
                    counterpartyBanned={counterpartyBanned}
                    {...interactionProps}
                  />
                </div>
              )
            }
            if (entry.type === "listingGroup") {
              const lead = entry.messages[0]!
              const lastInGroup = entry.messages[entry.messages.length - 1]!
              const isOwn = (lead.senderId || lead.user?.name) === username
              const name = isOwn ? (selfProfile?.username || "You") : (otherProfile?.username || "User")
              const avatar = isOwn ? null : getAvatarUrl(lead.user?.avatar_url || otherProfile?.avatar_url || "")
              const prevMessage = entry.firstIndex > 0 ? allMessages[entry.firstIndex - 1] : null
              const afterIndex = entry.firstIndex + entry.messages.length
              const afterMessage = afterIndex < allMessages.length ? allMessages[afterIndex] : null
              const senderKey = lastInGroup.senderId || lastInGroup.user?.name
              const sameSenderAfter = Boolean(
                afterMessage && (afterMessage.senderId || afterMessage.user?.name) === senderKey,
              )
              const sameDayAfter = Boolean(
                afterMessage &&
                  getDateKey(new Date(afterMessage.createdAt)) === getDateKey(new Date(lastInGroup.createdAt)),
              )
              const showAvatar = shouldShowIncomingChatAvatar({
                isOwnMessage: isOwn,
                sameSenderNext: sameSenderAfter,
                sameDayNext: sameDayAfter,
              })
              const isNewSender = !prevMessage || prevMessage.user?.name !== lead.user?.name
              const wrapperClass = entry.firstIndex === 0 ? "" : isNewSender ? "mt-4" : "mt-1"
              const leadKey = String(lead.clientKey ?? lead.id)
              const interactionProps = getMessageInteractionProps(lead, isOwn, name, avatar, leadKey, showAvatar, false)
              return (
                <div key={entry.key} className={wrapperClass}>
                  <ChatMessageItem
                    message={lead}
                    isOwnMessage={isOwn}
                    showHeader={showAvatar}
                    showAvatar={showAvatar}
                    displayName={name}
                    avatarUrl={avatar}
                    roomName={roomName}
                    currentUserId={username}
                    otherParticipantId={otherParticipantId}
                    showTail={false}
                    onOrderCardClick={onNavigateAway}
                    listingGroup={entry.messages}
                    counterpartyBanned={counterpartyBanned}
                    {...interactionProps}
                  />
                </div>
              )
            }
            const { message, index } = entry
            const prevMessage = index > 0 ? allMessages[index - 1] : null
            const nextMessage = index + 1 < allMessages.length ? allMessages[index + 1] : null
            const isOwn = (message.senderId || message.user?.name) === username
            const sameSenderNext = nextMessage && (nextMessage.senderId || nextMessage.user?.name) === (message.senderId || message.user?.name)
            const sameDayNext = nextMessage && getDateKey(new Date(nextMessage.createdAt)) === getDateKey(new Date(message.createdAt))
            const nextIsStacked = Boolean(
              nextMessage &&
                sameSenderNext &&
                sameDayNext &&
                (isImageOnlyMessage(nextMessage) || isListingMessage(nextMessage)),
            )
            const showAvatar = shouldShowIncomingChatAvatar({
              isOwnMessage: isOwn,
              sameSenderNext: Boolean(sameSenderNext),
              sameDayNext: Boolean(sameDayNext),
              nextIsStacked,
            })
            const showHeader = showAvatar
            const nextSender = nextMessage ? (nextMessage.senderId || nextMessage.user?.name) : null
            const currentSender = message.senderId || message.user?.name
            const currentNormalized = String(message.content ?? "").trim().toLowerCase()
            const nextNormalized = String(nextMessage?.content ?? "").trim().toLowerCase()
            const currentIsTextBubble = !((message as any).listing || (message as any).imageUrl || (message as any).order || currentNormalized === "[listing]" || currentNormalized === "[order]")
            const nextIsTextBubble = nextMessage
              ? !((nextMessage as any).listing || (nextMessage as any).imageUrl || (nextMessage as any).order || nextNormalized === "[listing]" || nextNormalized === "[order]")
              : false
            const showTail =
              currentIsTextBubble &&
              (!nextMessage || nextSender !== currentSender || !sameDayNext || !nextIsTextBubble)
            const name = isOwn 
              ? (selfProfile?.username || 'You')
              : (otherProfile?.username || 'User')
            const avatar = isOwn 
              ? null 
              : getAvatarUrl(message.user?.avatar_url || otherProfile?.avatar_url || '')
            const rawKey = (message.clientKey || message.id) as string | undefined
            const mKey = rawKey ?? entry.key
            const isNewSender = !prevMessage || prevMessage.user?.name !== message.user?.name
            const prevHadAttachment = !!(prevMessage && ((prevMessage as any).listing || (prevMessage as any).imageUrl))
            const bothAttachments = !!(prevMessage && ((prevMessage as any).listing || (prevMessage as any).imageUrl)) && (((message as any).listing || (message as any).imageUrl))
            const isTextOnly = !(message as any).listing && !(message as any).imageUrl
            const extraGapAfterAttachment = !isNewSender && prevHadAttachment && isTextOnly
            const wrapperMargin = index === 0 ? '' : (isNewSender ? 'mt-4' : 'mt-1')
            const wrapperClass = [wrapperMargin, (extraGapAfterAttachment || bothAttachments) ? 'mt-2' : ''].filter(Boolean).join(' ')
            const interactionProps = getMessageInteractionProps(message, isOwn, name, avatar, mKey, showAvatar, showTail)
            return (
              <div key={entry.key} className={wrapperClass}>
                <ChatMessageItem 
                  message={message} 
                  isOwnMessage={isOwn} 
                  showHeader={showHeader}
                  showAvatar={showAvatar}
                  displayName={name} 
                  avatarUrl={avatar}
                  roomName={roomName}
                  currentUserId={username}
                  otherParticipantId={otherParticipantId}
                  showTail={showTail}
                  onOrderCardClick={onNavigateAway}
                  counterpartyBanned={counterpartyBanned}
                  {...interactionProps}
                />
              </div>
            )
          })}
        </div>
        ) : null}
            </div>
      </div>

      {!loadError ? (
      <>
      <motion.div
        className={cn(
          composerLayer,
          "bottom-0 left-0 right-0 w-full pointer-events-none overflow-hidden backdrop-blur-md",
          isDialog ? "z-50" : "z-30",
          galleryContext?.isGalleryOpen && "opacity-0",
        )}
        style={dockComposer ? chatConversationFooterFadeStyle : { ...chatConversationFooterFadeStyle, bottom: 0 }}
        animate={{
          height: showListingSelector || selectedImages.length > 0 || selectedListings.length > 0 || selectedOrder ? 280 : 82,
        }}
        transition={{ type: "tween", duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      />

      <div
        ref={composerRef}
        className={cn(
          composerLayer,
          "bottom-0 left-0 right-0 isolate z-40 w-full px-4 pb-4",
          composerHasOpaqueStack && "bg-background",
          !dockComposer && "mx-auto max-w-5xl",
          showListingSelector ? "pt-0" : "pt-4",
          isDialog ? "z-90" : undefined,
        )}
      >
        {showListingSelector && (
          <ChatListingBottomSheet
            currentUserId={username}
            selectedIds={selectedListings.map((l) => l.id)}
            onToggle={toggleListing}
            onClose={() => setShowListingSelector(false)}
          />
        )}
        {composerError && (
          <ModerationNotice
            message={composerError.message}
            type={composerError.type}
            className="mb-4"
          />
        )}
        <MessagingSafetyBanner visible={guidelines.showSafetyBanner} onDismiss={guidelines.dismissBanner} />
        {replyPreview ? (
          <ChatComposerReplyStrip
            replyTo={replyPreview}
            onDismiss={clearReply}
            fallbackSenderName={replyFallbackSenderName}
            senderContext={replySenderContext}
          />
        ) : null}
        <ChatComposer
          value={newMessage}
          onChange={setNewMessage}
          onSend={submitMessage}
          onPhotoClick={() => fileInputRef.current?.click()}
          onOpenListingPicker={() => setShowListingSelector(true)}
          onSellClick={CHAT_SELL_ITEM_ENABLED ? () => setShowSellSheet(true) : undefined}
          sellItemEnabled={CHAT_SELL_ITEM_ENABLED}
          selectedImages={selectedImages}
          onRemoveImage={removeImage}
          selectedListings={selectedListings}
          onRemoveListing={removeListing}
          selectedOrder={selectedOrder}
          onRemoveOrder={() => setSelectedOrder(null)}
          disabled={isLoading || guidelines.needsAcceptance}
          maxLength={MAX_MESSAGE_LENGTH}
          onClearError={() => setComposerError(null)}
          listingPicker={null}
          trapFocusWhenReportOpen={reportDialogOpen || messageReportOpen}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addImages(e.target.files)
            e.target.value = ""
          }}
        />
      </div>
      </>
      ) : null}
      <MessagingGuidelinesSheet
        open={guidelines.sheetOpen}
        onAccept={guidelines.acceptGuidelines}
        onGoBack={() => {
          guidelines.goBackFromGuidelines()
          onBack?.()
        }}
      />
      {overlayTarget ? (
        <ChatMessageOverlay
          visible
          message={overlayTarget.message}
          anchor={overlayTarget.anchor}
          isOwnMessage={overlayTarget.ctx.isOwnMessage}
          displayName={
            overlayTarget.ctx.isOwnMessage
              ? selfProfile?.username ?? "You"
              : overlayTarget.ctx.reportedUsername ?? overlayTarget.message.user?.name ?? ""
          }
          avatarUrl={
            getAvatarUrl(
              overlayTarget.ctx.reportedAvatarUrl ?? overlayTarget.message.user?.avatar_url,
            ) ?? undefined
          }
          showAvatar={overlayTarget.layout.showAvatar}
          showTail={overlayTarget.layout.showTail}
          replyPreview={overlayTarget.layout.replyPreview ?? null}
          reactions={getMessageReactions(overlayTarget.message)}
          viewerReaction={getViewerReaction(overlayTarget.message)}
          canReport={Boolean(buildMessageReportPayload(overlayTarget.message, overlayTarget.ctx))}
          roomName={roomName}
          currentUserId={username}
          onDismiss={dismissOverlay}
          onReply={handleOverlayReply}
          onReport={handleOverlayReport}
          onCopy={() => void handleOverlayCopy()}
          onReaction={handleReaction}
        />
      ) : null}
      {messageReportTarget ? (
        <ReportDialog
          open={messageReportOpen}
          onOpenChangeAction={(open) => {
            if (!open) closeMessageReport()
          }}
          itemType={messageReportTarget.itemType}
          itemId={messageReportTarget.itemId}
          itemTitle={messageReportTarget.itemTitle}
          currentUserId={username}
          reportedUserId={messageReportTarget.reportedUserId}
          reportedUsername={messageReportTarget.reportedUsername ?? undefined}
          reportedAvatarUrl={messageReportTarget.reportedAvatarUrl}
        />
      ) : null}
      {/* Auth prompt fallback (should rarely show; most chat entry points already gate unauthenticated users) */}
      <AuthModal
        isOpen={showAuthModal}
        onCloseAction={() => setShowAuthModal(false)}
        title={otherProfile?.username ? `Chat with ${otherProfile.username}` : undefined}
  username={otherProfile?.username}
  userImage={getAvatarUrl(otherProfile?.avatar_url ?? null) ?? undefined}
        message="Please log in to send a message."
        callbackUrl={`/chat/${encodeURIComponent(roomName)}`}
      />
    </div>
  )
}
